import { useMemo, useState } from 'react'
import { errorMessage } from '../pb'
import {
  categoryList,
  contributorName,
  matchesQuery,
  orNull,
  totalMinutes,
} from '../recipes'
import type { Recipe } from '../recipes'
import { authorName, deleteRequest, postRequest, relativeTime, setAnswered, toggleAmen } from '../sisters'
import type { BoardEntry } from '../sisters'
import { categoryLabel } from '../submitRecipe'
import { useToast } from '../shell/Toast'
import { Modal } from '../shell/Modal'
import { SubmitRecipeForm } from '../components/SubmitRecipeForm'
import type { MembersData } from './useMembersData'

/**
 * The feed: prayers and recipes together, newest first, in the shell's post grammar.
 *
 * One list rather than two panels, because that is what the sisters do together: a request goes
 * up, a recipe goes up, both are read in the same scroll. The scope narrows it to one kind for
 * the table and the board tabs, and the chips narrow it further.
 *
 * Ordering stays newest first by default. A board sorted by amens turns prayer into a ranking,
 * and the request nobody has answered is the one that most needs to be seen. "Most amens" is
 * offered, but it is a choice she makes, never the default.
 */

export type Scope = 'all' | 'recipes' | 'prayers'
type Filter = 'all' | 'prayers' | 'recipes' | 'sabbath' | 'answered' | 'mine'
type Sort = 'newest' | 'sabbath' | 'amens'

const HOME_LIMIT = 7

type Item = { kind: 'prayer'; created: string; entry: BoardEntry } | { kind: 'recipe'; created: string; recipe: Recipe }

export function Feed({
  scope,
  data,
  myUserId,
  myName,
  daysToSabbath,
}: {
  scope: Scope
  data: MembersData
  myUserId: string
  myName: string
  daysToSabbath: number | null
}) {
  const toast = useToast()
  const { board, recipes, mine, refreshBoard, refreshRecipes } = data
  const [filter, setFilter] = useState<Filter>('all')
  const [sort, setSort] = useState<Sort>(
    scope === 'recipes' && daysToSabbath !== null && daysToSabbath > 0 && daysToSabbath <= 2 ? 'sabbath' : 'newest',
  )
  const [query, setQuery] = useState('')
  const [draft, setDraft] = useState('')
  const [posting, setPosting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [openRecipe, setOpenRecipe] = useState<Recipe | null>(null)
  const [sharing, setSharing] = useState(false)

  const items = useMemo<Item[]>(() => {
    const list: Item[] = []
    const wantPrayers = scope !== 'recipes' && filter !== 'recipes' && filter !== 'sabbath'
    const wantRecipes = scope !== 'prayers' && filter !== 'prayers' && filter !== 'answered'

    if (wantPrayers && board) {
      for (const entry of board) {
        if (filter === 'answered' && !entry.is_answered) continue
        if (filter === 'mine' && entry.author !== myUserId) continue
        if (query && !entry.body.toLowerCase().includes(query.toLowerCase())) continue
        list.push({ kind: 'prayer', created: entry.created, entry })
      }
    }
    if (wantRecipes) {
      const source = filter === 'mine' ? (mine ?? []) : (recipes ?? [])
      for (const recipe of source) {
        if (filter === 'sabbath' && !recipe.sabbath_prep) continue
        if (query && !matchesQuery(recipe, query)) continue
        list.push({ kind: 'recipe', created: recipe.created, recipe })
      }
    }

    list.sort((a, b) => b.created.localeCompare(a.created))
    if (sort === 'sabbath') {
      list.sort((a, b) => Number(b.kind === 'recipe' && b.recipe.sabbath_prep) - Number(a.kind === 'recipe' && a.recipe.sabbath_prep))
    } else if (sort === 'amens') {
      list.sort((a, b) => (b.kind === 'prayer' ? b.entry.amenCount : -1) - (a.kind === 'prayer' ? a.entry.amenCount : -1))
    }
    // Home is a glance, not the archive: the seven most recent, and the table and the board
    // tabs hold the rest. Any chip, search or sort is her asking for more, so those show it all.
    const glance = scope === 'all' && filter === 'all' && sort === 'newest' && !query.trim()
    return glance ? list.slice(0, HOME_LIMIT) : list
  }, [board, recipes, mine, scope, filter, sort, query, myUserId])

  const loading = (scope !== 'recipes' && board === null) || (scope !== 'prayers' && recipes === null)

  async function post(event: React.FormEvent) {
    event.preventDefault()
    const body = draft.trim()
    if (!body) return
    setPosting(true)
    setError(null)
    try {
      await postRequest(myUserId, body)
      setDraft('')
      await refreshBoard()
      toast('Posted to the sisters')
    } catch (err) {
      setError(errorMessage(err, 'Could not post that'))
    } finally {
      setPosting(false)
    }
  }

  async function act(work: () => Promise<void>, said?: string) {
    try {
      await work()
      await refreshBoard()
      if (said) toast(said)
    } catch (err) {
      setError(errorMessage(err, 'Could not save that'))
    }
  }

  const chips: { id: Filter; label: string }[] =
    scope === 'all'
      ? [
          { id: 'all', label: 'Everything' },
          { id: 'prayers', label: 'Prayers' },
          { id: 'recipes', label: 'Recipes' },
          { id: 'sabbath', label: 'Sabbath prep' },
          { id: 'answered', label: 'Answered' },
          { id: 'mine', label: 'Mine' },
        ]
      : scope === 'recipes'
        ? [
            { id: 'all', label: 'The collection' },
            { id: 'sabbath', label: 'Sabbath prep' },
            { id: 'mine', label: 'Mine' },
          ]
        : [
            { id: 'all', label: 'The board' },
            { id: 'answered', label: 'Answered' },
            { id: 'mine', label: 'Mine' },
          ]

  const pendingCount = (mine ?? []).filter((r) => r.status !== 'approved').length

  return (
    <>
      {scope !== 'recipes' && (
        <form className="card composer" onSubmit={post}>
          <div className="composer-row">
            <span className="avatar is-lg" aria-hidden="true">
              {initialOf(myName)}
            </span>
            <div className="composer-main">
              <label className="visually-hidden" htmlFor="composer">
                Ask for prayer
              </label>
              <textarea
                id="composer"
                rows={2}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder={`Ask the sisters to pray with you${myName ? `, ${firstName(myName)}` : ''}`}
              />
              <div className="composer-foot">
                <div className="mode-chips">
                  <span className="mode-chip is-on">
                    <i className="ph-bold ph-hands-praying" aria-hidden="true" />
                    Prayer request
                  </span>
                  <button className="mode-chip" type="button" onClick={() => setSharing(true)}>
                    <i className="ph-bold ph-bowl-food" aria-hidden="true" />
                    Recipe
                  </button>
                </div>
                <button className="btn-primary" type="submit" disabled={posting || !draft.trim()}>
                  {posting ? 'Posting' : 'Post'}
                  <i className="ph-bold ph-paper-plane-right" aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>
        </form>
      )}

      {scope === 'recipes' && (
        <div className="filters-row">
          <div>
            <h2 className="panel-title">Recipes</h2>
            <p className="panel-note">
              {daysToSabbath !== null && daysToSabbath > 0 && daysToSabbath <= 2
                ? 'The Sabbath is close, so the recipes that keep come first.'
                : 'Shared recipes, each read through by a sister and checked against Leviticus 11.'}
            </p>
          </div>
          <button className="btn-primary" type="button" onClick={() => setSharing(true)}>
            <i className="ph-bold ph-plus" aria-hidden="true" />
            Share a recipe
          </button>
        </div>
      )}

      {error && <p className="notice">{error}</p>}

      <div className="filters-row">
        <div className="filter-chips" role="tablist">
          {chips.map((chip) => (
            <button
              key={chip.id}
              className={filter === chip.id ? 'filter-chip is-on' : 'filter-chip'}
              type="button"
              role="tab"
              aria-selected={filter === chip.id}
              onClick={() => setFilter(chip.id)}
            >
              {chip.label}
              {chip.id === 'mine' && pendingCount > 0 && scope !== 'prayers' ? ` · ${pendingCount} waiting` : ''}
            </button>
          ))}
        </div>
        <div className="sort">
          <label className="search-inline">
            <i className="ph-bold ph-magnifying-glass" aria-hidden="true" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={scope === 'prayers' ? 'Search the board' : 'An ingredient, a sister, a word'}
              aria-label="Search"
            />
          </label>
          <span>Sort</span>
          <select className="sort-select" value={sort} onChange={(e) => setSort(e.target.value as Sort)} aria-label="Sort by">
            <option value="newest">Newest</option>
            {scope !== 'prayers' && <option value="sabbath">Sabbath prep first</option>}
            {scope !== 'recipes' && <option value="amens">Most amens</option>}
          </select>
        </div>
      </div>

      <div className="feed">
        {loading ? (
          <p className="quiet-center">One moment</p>
        ) : items.length === 0 ? (
          <p className="quiet-center">{emptyWords(scope, filter, query)}</p>
        ) : (
          items.map((item) =>
            item.kind === 'prayer' ? (
              <PrayerPost
                key={`p-${item.entry.id}`}
                entry={item.entry}
                mine={item.entry.author === myUserId}
                onAmen={() => act(() => toggleAmen(item.entry.id, myUserId, item.entry.myAmenId))}
                onAnswered={() =>
                  act(
                    () => setAnswered(item.entry.id, !item.entry.is_answered),
                    item.entry.is_answered ? 'Marked not answered yet' : 'Marked answered',
                  )
                }
                onRemove={() => act(() => deleteRequest(item.entry.id), 'Removed')}
              />
            ) : (
              <RecipePost key={`r-${item.recipe.id}`} recipe={item.recipe} onOpen={() => setOpenRecipe(item.recipe)} />
            ),
          )
        )}
      </div>

      {openRecipe && <RecipeModal recipe={openRecipe} onClose={() => setOpenRecipe(null)} />}

      {sharing && (
        <Modal title="Share a recipe" sub="Checked against Leviticus 11 as you write" icon="ph-bowl-food" wide onClose={() => setSharing(false)}>
          <SubmitRecipeForm
            myUserId={myUserId}
            onSubmitted={() => {
              void refreshRecipes()
              toast('Sent for review')
            }}
          />
        </Modal>
      )}
    </>
  )
}

function PrayerPost({
  entry,
  mine,
  onAmen,
  onAnswered,
  onRemove,
}: {
  entry: BoardEntry
  mine: boolean
  onAmen: () => void
  onAnswered: () => void
  onRemove: () => void
}) {
  const name = authorName(entry)
  const classes = ['card', 'post']
  if (entry.is_answered) classes.push('is-answered')
  else if (mine) classes.push('is-mine')

  return (
    <article className={classes.join(' ')}>
      <div className="post-head">
        <div className="post-who">
          <span className={`avatar is-lg ${tone(name)}`} aria-hidden="true">
            {initialOf(name)}
          </span>
          <div>
            <div className="post-name-row">
              <span className="post-name">{mine ? 'You' : name}</span>
              <span className={entry.is_answered ? 'post-tag tag-gold' : 'post-tag tag-rose'}>
                {entry.is_answered ? 'Answered' : 'Prayer'}
              </span>
            </div>
            <div className="post-meta">
              <span>{relativeTime(entry.created)}</span>
              <span>•</span>
              <span>Sisters</span>
            </div>
          </div>
        </div>
        {entry.is_answered ? (
          <span className="post-side is-gold">
            <i className="ph-fill ph-sparkle" aria-hidden="true" />
            Answered
          </span>
        ) : entry.amenCount > 0 ? (
          <span className="post-side">
            <i className="ph-bold ph-hands-praying" aria-hidden="true" />
            {entry.amenCount} praying
          </span>
        ) : null}
      </div>

      <blockquote className="post-quote">{entry.body}</blockquote>

      <div className="post-foot">
        <div className="post-acts">
          <button
            className={entry.myAmenId ? 'act is-on' : 'act is-rose'}
            type="button"
            aria-pressed={entry.myAmenId !== null}
            onClick={onAmen}
          >
            <i className={entry.myAmenId ? 'ph-fill ph-heart' : 'ph-bold ph-heart'} aria-hidden="true" />
            Amen
            {entry.amenCount > 0 && <span className="counter">{entry.amenCount}</span>}
          </button>
          {mine && (
            <button className="act is-gold" type="button" onClick={onAnswered}>
              <i className="ph-bold ph-sparkle" aria-hidden="true" />
              {entry.is_answered ? 'Not answered yet' : 'Mark answered'}
            </button>
          )}
        </div>
        {mine && (
          <button className="act" type="button" onClick={onRemove}>
            <i className="ph-bold ph-trash-simple" aria-hidden="true" />
            Remove
          </button>
        )}
      </div>
    </article>
  )
}

function RecipePost({ recipe, onOpen }: { recipe: Recipe; onOpen: () => void }) {
  const name = contributorName(recipe)
  const minutes = totalMinutes(recipe)
  const servings = orNull(recipe.servings)
  const ingredients = recipe.ingredients ?? []
  const waiting = recipe.status === 'pending'
  const sentBack = recipe.status === 'rejected'

  return (
    <article className="card post">
      <div className="post-head">
        <div className="post-who">
          <span className={`avatar is-lg ${tone(name)}`} aria-hidden="true">
            {initialOf(name)}
          </span>
          <div>
            <div className="post-name-row">
              <span className="post-name">{name}</span>
              {recipe.sabbath_prep && <span className="post-tag tag-gold">Sabbath prep</span>}
              {waiting && <span className="post-tag tag-waiting">Waiting</span>}
              {sentBack && <span className="post-tag tag-waiting">Sent back</span>}
            </div>
            <div className="post-meta">
              <span>{relativeTime(recipe.created)}</span>
              <span>•</span>
              <span>Recipe</span>
              {minutes !== null && (
                <>
                  <span>•</span>
                  <span>{minutes} min</span>
                </>
              )}
              {servings !== null && (
                <>
                  <span>•</span>
                  <span>Serves {servings}</span>
                </>
              )}
            </div>
          </div>
        </div>
        <button className={recipe.clean_verified ? 'post-side is-olive' : 'post-side'} type="button" onClick={onOpen}>
          <i className={recipe.clean_verified ? 'ph-bold ph-seal-check' : 'ph-bold ph-bowl-food'} aria-hidden="true" />
          {recipe.clean_verified ? 'Clean · Lev 11' : 'Open'}
          <i className="ph-bold ph-arrow-right arrow" aria-hidden="true" />
        </button>
      </div>

      <h3 className="post-title">{recipe.title}</h3>
      {recipe.instructions && <p className="post-body">{excerpt(recipe.instructions)}</p>}

      {sentBack && recipe.rejection_feedback && <p className="notice">Sent back with a note: {recipe.rejection_feedback}</p>}
      {waiting && <p className="notice">Waiting to be read through. It joins the table once a sister has looked at it.</p>}

      {ingredients.length > 0 && (
        <div className={recipe.clean_verified ? 'embed' : 'embed is-rose'}>
          <div className="embed-head">
            <span className="embed-label">
              <i className="ph-fill ph-scroll" aria-hidden="true" />
              {ingredients.length === 1 ? 'One ingredient' : `${ingredients.length} ingredients`}
              {recipe.clean_verified ? ', all clean' : ''}
            </span>
            <button className="embed-link" type="button" onClick={onOpen}>
              Open the recipe
              <i className="ph-bold ph-arrow-up-right" aria-hidden="true" />
            </button>
          </div>
          <p className="embed-text">
            {sentence(ingredients.map((item) => item.name))}
            {recipe.clean_verified ? ' Nothing in Leviticus 11 speaks against any of them.' : ''}
          </p>
        </div>
      )}

      <div className="post-foot">
        <div className="post-acts">
          <button className="act is-rose" type="button" onClick={onOpen}>
            <i className="ph-bold ph-list-checks" aria-hidden="true" />
            Method
          </button>
          {categoryList(recipe).length > 0 && (
            <span className="act">
              <i className="ph-bold ph-tag" aria-hidden="true" />
              <span className="plain">{categoryList(recipe).map(categoryLabel).join(' · ')}</span>
            </span>
          )}
        </div>
        {recipe.verified && (
          <span className="post-fine">
            <i className="ph-fill ph-check-circle" aria-hidden="true" />
            Kitchen-tested
          </span>
        )}
      </div>
    </article>
  )
}

export function RecipeModal({ recipe, onClose }: { recipe: Recipe; onClose: () => void }) {
  const ingredients = recipe.ingredients ?? []
  const calories = orNull(recipe.calories_per_serving)
  const servings = orNull(recipe.servings)
  const minutes = totalMinutes(recipe)
  const facts = [
    servings !== null ? `Serves ${servings}` : null,
    minutes !== null ? `${minutes} min` : null,
    calories !== null ? `${calories} cal a serving` : null,
    recipe.difficulty || null,
  ].filter(Boolean)

  return (
    <Modal
      title={recipe.title}
      sub={[contributorName(recipe), recipe.sabbath_prep ? 'Sabbath prep' : null, recipe.clean_verified ? 'Checked against Leviticus 11' : null]
        .filter(Boolean)
        .join(' · ')}
      icon={recipe.clean_verified ? 'ph-seal-check' : 'ph-bowl-food'}
      tone={recipe.clean_verified ? 'olive' : 'rose'}
      wide
      onClose={onClose}
      foot={
        <>
          <span className="card-note">{facts.join(' · ')}</span>
          <button className="btn-ghost" type="button" onClick={onClose}>
            Close
          </button>
        </>
      }
    >
      <div className="pair">
        <div className="well">
          <div className="well-label">Ingredients</div>
          {ingredients.length > 0 ? (
            <ul className="ingredients">
              {ingredients.map((item, index) => (
                <li key={`${item.name}-${index}`}>
                  <span className="amount">{[item.amount, item.unit].filter(Boolean).join(' ')}</span>
                  <span>{item.name}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="card-note">No ingredient list was given.</p>
          )}
        </div>
        <div className={recipe.clean_verified ? 'well is-olive' : 'well'}>
          <div className="well-label">The check</div>
          <p className="embed-text">
            {recipe.clean_verified
              ? 'Every ingredient was checked against Leviticus 11 and nothing in the text speaks against it.'
              : 'This recipe has not been marked clean by a reviewer yet.'}
          </p>
          <div className="embed-refs">Leviticus 11 · Deuteronomy 14</div>
        </div>
      </div>
      {recipe.instructions && (
        <div className="well">
          <div className="well-label">Method</div>
          <p className="instructions">{recipe.instructions}</p>
        </div>
      )}
      {categoryList(recipe).length > 0 && <p className="categories">{categoryList(recipe).map(categoryLabel).join(' · ')}</p>}
    </Modal>
  )
}

function emptyWords(scope: Scope, filter: Filter, query: string): string {
  if (query.trim()) return 'Nothing matches that. Try a different word.'
  if (filter === 'mine') return 'Nothing of yours here yet.'
  if (filter === 'answered') return 'No answered prayers yet. When one is, it will be here.'
  if (scope === 'prayers') return 'The board is quiet. Post the first request and your sisters will see it.'
  if (scope === 'recipes') return 'Nothing on the table yet. Yours could be the first.'
  return 'Nothing here yet.'
}

function excerpt(text: string): string {
  const flat = text.replace(/\s+/g, ' ').trim()
  if (flat.length <= 220) return flat
  const cut = flat.slice(0, 220)
  return `${cut.slice(0, cut.lastIndexOf(' '))}…`
}

function sentence(names: string[]): string {
  const clean = names.map((n) => n.trim()).filter(Boolean)
  if (clean.length === 0) return ''
  const [head, ...rest] = clean
  const first = (head ?? '').charAt(0).toUpperCase() + (head ?? '').slice(1)
  return [first, ...rest].join(', ') + '.'
}

export function initialOf(name: string): string {
  const trimmed = name.trim()
  return trimmed ? trimmed.charAt(0).toUpperCase() : 'S'
}

export function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] ?? ''
}

/** A stable avatar tone per name, so a sister keeps her colour across the feed. */
function tone(name: string): string {
  const tones = ['', 'tone-gold', 'tone-olive', 'tone-plum']
  let hash = 0
  for (const ch of name) hash = (hash * 31 + ch.charCodeAt(0)) >>> 0
  return tones[hash % tones.length] ?? ''
}
